import {
    BUTTON_VARIANTS,
    HTTP_CONFLICT,
    RETIRED_BUTTON_VARIANT,
    THEME_CONFIG_KEYS,
    THEME_DEFAULTS,
    THEME_EDITOR_SECTION_IDS,
    THEME_SECTION_CONFIG_FIELDS,
} from './constants';

// The only place that knows the shape of a theme API response — components read
// the view model built here, never a raw response field.

// Stored themes may still carry the retired 'text' button variant.
export const normalizeButtonVariant = (variant) =>
    variant === RETIRED_BUTTON_VARIANT ? BUTTON_VARIANTS.OUTLINED : variant;

// Legacy rows can still arrive snake_case, so normalize before merging.
const toCamelCase = (key) => key.replace(/_([a-z])/g, (_match, char) => char.toUpperCase());

const camelCaseKeys = (source = {}) =>
    Object.entries(source).reduce((acc, [key, value]) => {
        acc[toCamelCase(key)] = value;
        return acc;
    }, {});

const pick = (source, keys) =>
    keys.reduce((acc, key) => {
        if (source?.[key] !== undefined) {
            acc[key] = source[key];
        }
        return acc;
    }, {});

export const buildThemeConfig = (config) => {
    const merged = { ...THEME_DEFAULTS, ...camelCaseKeys(config) };

    return {
        ...merged,
        button: {
            ...THEME_DEFAULTS.button,
            ...merged.button,
            variant: normalizeButtonVariant(
                merged.button?.variant ?? THEME_DEFAULTS.button.variant
            ),
        },
    };
};

export const themeFromApi = (theme = {}) => ({
    nanoid: theme.nanoid,
    name: theme.name ?? '',
    isDefault: Boolean(theme.is_default),
    config: buildThemeConfig(theme.config),
    logoUrl: theme.logo_url ?? null,
    workflowsCount: theme.workflows_count ?? 0,
    workflows: theme.workflows ?? [],
    createdAt: theme.created_at ?? null,
    updatedAt: theme.updated_at ?? null,
});

// GET /admin/themes  -> { themes: [...] }
export const themesFromApi = (response) => (response?.themes ?? []).map(themeFromApi);

// GET one wraps as { theme }; create/update/duplicate/set-default don't.
export const themeFromApiResponse = (response) => {
    const theme = response?.theme ?? response;

    return theme?.nanoid ? themeFromApi(theme) : null;
};

// `logo` is three-state: base64 replaces, null removes, omitted leaves as is.
export const themeToApi = ({ name, config, logo } = {}) => {
    const body = {};

    if (name !== undefined) {
        body.name = name;
    }

    if (config !== undefined) {
        body.config = pick(buildThemeConfig(config), THEME_CONFIG_KEYS);
    }

    if (logo !== undefined) {
        body.logo = logo;
    }

    return body;
};

// The API returns no permission flags; these are derived. Only delete is also
// guarded server-side (409) — everything a company owns is editable.
export const canEditTheme = (theme) => Boolean(theme?.nanoid);

export const canDuplicateTheme = () => true;

export const canSetThemeAsDefault = (theme) => !theme?.isDefault;

export const canDeleteTheme = (theme, themesCount = 0) =>
    theme?.workflowsCount === 0 && !theme?.isDefault && themesCount > 1;

// RTK Query moves the code to `originalStatus` when the body isn't JSON, which
// this backend does inconsistently.
export const isConflictError = (error) =>
    error?.status === HTTP_CONFLICT || error?.originalStatus === HTTP_CONFLICT;

// Every company has a default theme, so the list is never empty — this is the
// "no themes of its own yet" state.
export const hasOnlyDefaultTheme = (themes) =>
    themes.length > 0 && themes.every((theme) => theme.isDefault);

// Cuts the middle so both the start and the extension stay readable.
export const truncateFileName = (name, maxLength = 20) => {
    if (name.length <= maxLength) {
        return name;
    }

    const dotIndex = name.lastIndexOf('.');
    const extension = dotIndex > 0 && name.length - dotIndex <= 6 ? name.slice(dotIndex) : '';
    const base = extension ? name.slice(0, dotIndex) : name;
    const visible = Math.max(2, maxLength - extension.length - 1);
    const headLength = Math.ceil(visible / 2);

    return `${base.slice(0, headLength)}…${base.slice(-(visible - headLength))}${extension}`;
};

// The browser trusts the extension for file.type, so a renamed non-image only fails here.
export const canDecodeImage = (src) => {
    const image = new Image();
    image.src = src;
    // decode() resolves empty or rejects; map that to a yes/no.
    return image.decode().then(
        () => true,
        () => false
    );
};

// A saved logo is just a URL — no filename of its own, so recover one for display.
export const getFilenameFromUrl = (url) => {
    try {
        const { pathname } = new URL(url);
        return decodeURIComponent(pathname.split('/').pop() ?? '');
    } catch {
        return '';
    }
};

/**
 * Which section rail items should show the "changed" dot — each section's
 * own config keys (see THEME_SECTION_CONFIG_FIELDS) plus Logo, which lives
 * outside config on draft.logo directly.
 */
export const getChangedSectionIds = (draft, initial) => {
    const changed = new Set();

    if (draft.logo !== initial.logo) {
        changed.add(THEME_EDITOR_SECTION_IDS.LOGO);
    }

    Object.entries(THEME_SECTION_CONFIG_FIELDS).forEach(([sectionId, fields]) => {
        const isSectionChanged = fields.some(
            (field) => JSON.stringify(draft.config[field]) !== JSON.stringify(initial.config[field])
        );
        if (isSectionChanged) {
            changed.add(sectionId);
        }
    });

    return changed;
};
